// OWNER: Agent C. Small fetch hooks around lib/api.ts. No react-query — the
// app is opened a few times a week by one person, plain useEffect is enough
// and keeps the dependency list short.
import { useCallback, useEffect, useState, type DependencyList } from "react";
import { get, post, patch, del, ApiRequestError } from "./api";
import { useAuth } from "./auth";
import type {
  Account,
  CreateLoanPaymentBody,
  CreateTransactionBody,
  FxRates,
  HistoryRange,
  Holding,
  Loan,
  LoanPayment,
  Paginated,
  PortfolioSummary,
  SnapshotPoint,
  Transaction,
  User,
} from "@shared/types";
import type { z } from "zod";
import type {
  createAccountSchema,
  createHoldingSchema,
  createLoanSchema,
  createUserSchema,
  updateAccountSchema,
  updateHoldingSchema,
  updateLoanSchema,
} from "@shared/schemas";

export type CreateAccountBody = z.input<typeof createAccountSchema>;
export type UpdateAccountBody = z.input<typeof updateAccountSchema>;
export type CreateHoldingBody = z.input<typeof createHoldingSchema>;
export type UpdateHoldingBody = z.input<typeof updateHoldingSchema>;
export type CreateLoanBody = z.input<typeof createLoanSchema>;
export type UpdateLoanBody = z.input<typeof updateLoanSchema>;
export type CreateUserBody = z.input<typeof createUserSchema>;

export type AsyncState<T> =
  | { status: "loading" }
  | { status: "error"; error: ApiRequestError | Error }
  | { status: "success"; data: T };

export function useAsync<T>(fetcher: () => Promise<T>, deps: DependencyList): AsyncState<T> & { reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ status: "loading" });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    fetcher()
      .then((data) => {
        if (!cancelled) setState({ status: "success", data });
      })
      .catch((error: ApiRequestError | Error) => {
        if (!cancelled) setState({ status: "error", error });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

/**
 * Conservative while the session is still loading — no admin-only affordance
 * gets a flash of "enabled" before we actually know the role. Backed by
 * Agent A's real <AuthProvider>/useAuth() (lib/auth.tsx) now that it's
 * landed, so this reads the same cached session instead of firing its own
 * GET /api/auth/me.
 */
export function useIsAdmin(): boolean {
  const { user, status } = useAuth();
  return status === "authenticated" && user?.role === "admin";
}

export const usePortfolioSummary = () => useAsync<PortfolioSummary>(() => get("/portfolio/summary"), []);

export const usePortfolioHistory = (range: HistoryRange) =>
  useAsync<SnapshotPoint[]>(() => get(`/portfolio/history?range=${range}`), [range]);

export const useAccounts = () => useAsync<Account[]>(() => get("/accounts"), []);

export const createAccount = (body: CreateAccountBody) => post<Account>("/accounts", body);
export const updateAccount = (id: number, body: UpdateAccountBody) => patch<Account>(`/accounts/${id}`, body);
export const deleteAccount = (id: number) => del<{ ok: boolean }>(`/accounts/${id}`);

export const useHoldings = (accountId: number | undefined) =>
  useAsync<Holding[]>(() => (accountId ? get(`/holdings?accountId=${accountId}`) : Promise.resolve([])), [accountId]);

export const createHolding = (body: CreateHoldingBody) => post<Holding>("/holdings", body);
export const updateHolding = (id: number, body: UpdateHoldingBody) => patch<Holding>(`/holdings/${id}`, body);
export const deleteHolding = (id: number) => del<{ ok: boolean }>(`/holdings/${id}`);

/** No bulk holdings endpoint in the contract — fan out per account. Fine at
 * this scale (a handful of accounts), but worth flagging for a future v2. */
export function useAllHoldingsByAccount() {
  return useAsync<{ accounts: Account[]; holdingsByAccount: Map<number, Holding[]> }>(async () => {
    const accounts = await get<Account[]>("/accounts");
    const lists = await Promise.all(accounts.map((a) => get<Holding[]>(`/holdings?accountId=${a.id}`)));
    const holdingsByAccount = new Map(accounts.map((a, i) => [a.id, lists[i] ?? []]));
    return { accounts, holdingsByAccount };
  }, []);
}

export const useTransactions = (params: { holdingId?: number; from?: string; to?: string; limit?: number } = {}) =>
  useAsync<Paginated<Transaction>>(() => {
    const qs = new URLSearchParams();
    if (params.holdingId) qs.set("holdingId", String(params.holdingId));
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.limit) qs.set("limit", String(params.limit));
    const q = qs.toString();
    return get(`/transactions${q ? `?${q}` : ""}`);
  }, [params.holdingId, params.from, params.to, params.limit]);

export const createTransaction = (body: CreateTransactionBody) => post<Transaction>("/transactions", body);

export const useLoans = () => useAsync<Loan[]>(() => get("/loans"), []);

export const createLoan = (body: CreateLoanBody) => post<Loan>("/loans", body);
export const updateLoan = (id: number, body: UpdateLoanBody) => patch<Loan>(`/loans/${id}`, body);
export const deleteLoan = (id: number) => del<{ id: number }>(`/loans/${id}`);

export const useLoan = (id: number) => useAsync<Loan>(() => get(`/loans/${id}`), [id]);

export const useLoanPayments = (loanId: number) => useAsync<LoanPayment[]>(() => get(`/loans/${loanId}/payments`), [loanId]);

export const recordLoanPayment = (loanId: number, body: CreateLoanPaymentBody) =>
  post<LoanPayment>(`/loans/${loanId}/payments`, body);

export const useFxLatest = () => useAsync<FxRates>(() => get("/fx/latest"), []);

export const useAdminUsers = () => useAsync<User[]>(() => get("/admin/users"), []);

// New members always land as role "member" — the API can never mint a
// second admin (single-admin invariant enforced server-side).
export const createUser = (body: CreateUserBody) => post<User>("/admin/users", body);
